"use client";

import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Formik } from "formik";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SurveyTakingEndPage } from "@/app/(survey-taking)/_components/SurveyTakingEndPage";
import { SurveyTakingIntroPage } from "@/app/(survey-taking)/_components/SurveyTakingIntroPage";
import { SurveyTakingLayout } from "@/app/(survey-taking)/_components/SurveyTakingLayout";
import { SurveyTakingPage } from "@/app/(survey-taking)/_components/SurveyTakingPage/SurveyTakingPage";
import { useSubmitAnswersMutation } from "@/app/(survey-taking)/_hooks/useSubmitAnswersMutation";
import { useSurveyTaking } from "@/app/(survey-taking)/_hooks/useSurveyTaking";
import { useSurveyTakingQuery } from "@/app/(survey-taking)/_hooks/useSurveyTakingQuery";
import { useUserQuery } from "@/app/(survey-taking)/_hooks/useUserQuery";
import { useToasts } from "@/hooks/useToasts";
import type { SurveyPage } from "@/models/surveyPage";
import { useMainStore } from "@/store";
import { SURVEY_STATUS } from "@/utils/constants";
import { filterPage } from "@/utils/filter";
import { sortQuestions } from "@/utils/questions";
import { SurveyTakingError } from "../../_components/SurveyTakingError";
import { SurveyTakingSkeleton } from "../../_components/SurveyTakingSkeleton";

type SurveyTakingProps = {
   surveyId?: string;
};

export type SurveyTakingFormValues = {
   authentication_form: {
      firstName: string;
      lastName: string;
      email: string;
   };
   pages: SurveyPage[];
   startTimestamp: string | null;
   endTimestamp: string | null;
};

export const SurveyTaking = ({
   surveyId: surveyIdProp,
}: SurveyTakingProps = {}) => {
   const { t } = useTranslation();

   const router = useRouter();
   const params = useParams<{ surveyId: string }>();
   const surveyId = surveyIdProp || params?.surveyId;
   const searchParams = useSearchParams();
   const entryIdParam = searchParams?.get("entry_id");

   const queryClient = useQueryClient();

   const submitAnswersMutation = useSubmitAnswersMutation();

   const { setCanUserAnswerSurvey } = useMainStore();
   const { addSuccessToast, addErrorToast } = useToasts();
   const {
      pageNumber,
      handleStartSurvey,
      handleChangePage,
      validateform,
      errors,
      setPageNumber,
   } = useSurveyTaking();

   const {
      survey: currentSurvey,
      isLoading: isSurveyLoading,
      error: surveyError,
   } = useSurveyTakingQuery({
      surveyId: surveyId ?? "",
      entryId: entryIdParam ?? undefined,
      enabled: !!surveyId,
   });

   const { data: user } = useUserQuery({
      enabled: !!currentSurvey?.is_authenticable && !entryIdParam,
   });

   const [entryId, setEntryId] = useState("");
   const [isAuthenticatedInfo, setIsAuthenticatedInfo] = useState({
      firstName: "",
      lastName: "",
      email: "",
   });
   const [stopTimer, setStopTimer] = useState(false);

   const handleSubmitAnswers = (values: SurveyTakingFormValues) => {
      setStopTimer(true);

      submitAnswersMutation.mutate(
         {
            surveyId: surveyId ?? "",
            entryId: entryIdParam ?? undefined,
            values,
         },
         {
            onSuccess: () => {
               addSuccessToast(
                  t("survey.submitted.success", { ns: "notifications" }),
               );
               queryClient.invalidateQueries({
                  queryKey: ["surveys-infinite"],
               });
               queryClient.invalidateQueries({ queryKey: ["surveyResponses"] });
            },
            onError: (error) => {
               if (error.code === 403 && error.entryId) {
                  setEntryId(error.entryId);
                  return;
               }

               if (error.code === 422) {
                  router.replace("/error?type_error=max");
                  return;
               }

               addErrorToast(
                  t("survey.submitted.error", { ns: "notifications" }),
               );
            },
            onSettled: () => {
               setPageNumber((prev) => prev + 1);
               setCanUserAnswerSurvey(true);
            },
         },
      );
   };

   useEffect(() => {
      setIsAuthenticatedInfo({
         firstName: currentSurvey?.entry?.firstname || user?.firstname || "",
         lastName: currentSurvey?.entry?.lastname || user?.lastname || "",
         email: currentSurvey?.entry?.email || user?.email || "",
      });
   }, [
      user,
      currentSurvey?.entry?.firstname,
      currentSurvey?.entry?.lastname,
      currentSurvey?.entry?.email,
   ]);

   useEffect(() => {
      if (isSurveyLoading || !currentSurvey) return;

      if (surveyError) {
         return router.replace("/error?type_error=error");
      }

      if (
         currentSurvey.max_entries !== null &&
         currentSurvey.entries_count >= currentSurvey.max_entries
      ) {
         return router.replace("/error?type_error=max");
      }

      if (
         currentSurvey.sessions?.length !== 0 &&
         currentSurvey.sessions?.every(
            (session) => session.capacity === session.total_guests_count,
         )
      ) {
         return router.replace("/error?type_error=sessionFull");
      }

      if (
         currentSurvey.expires_at &&
         currentSurvey.expires_at <=
            dayjs(Date.now()).format("YYYY-MM-DD HH:mm:ss")
      ) {
         return router.replace("/error?type_error=tooLate");
      }

      switch (currentSurvey?.status?.label) {
         case SURVEY_STATUS.PAUSED:
            return router.replace("/error?type_error=error");
         case SURVEY_STATUS.DRAFT:
            return router.replace("/error");
         case SURVEY_STATUS.CLOSED:
            return router.replace("/error?type_error=tooLate");
      }
   }, [currentSurvey, isSurveyLoading, surveyError, router]);

   return currentSurvey ? (
      <Formik
         initialValues={
            {
               authentication_form: currentSurvey?.is_authenticable
                  ? {
                       ...isAuthenticatedInfo,
                    }
                  : undefined,
               pages:
                  currentSurvey?.pages?.map((page) => ({
                     ...page,
                     questions: sortQuestions(page.questions),
                     shuffled_questions:
                        page.shuffled_questions !== undefined
                           ? page.shuffled_questions
                           : sortQuestions(page.questions),
                  })) ?? [],
               startTimestamp: null,
               endTimestamp: null,
            } as SurveyTakingFormValues
         }
         validate={(values) => validateform({ currentSurvey, values })}
         enableReinitialize
         validateOnChange={false}
         onSubmit={(values) => handleSubmitAnswers(values)}
      >
         {(formik) => (
            <SurveyTakingLayout
               entry={currentSurvey.entry}
               currentPage={pageNumber}
               totalPage={formik.values.pages?.filter(filterPage)?.length}
               currentSurvey={currentSurvey}
               onPageChange={(isNextPage) =>
                  handleChangePage({ currentSurvey, isNextPage, formik })
               }
               formik={formik}
               handleSubmitAnswers={handleSubmitAnswers}
               isSubmitting={submitAnswersMutation.isPending}
               stopTimer={stopTimer}
            >
               <SurveyTakingIntroPage
                  currentSurvey={currentSurvey}
                  handleMoveToNextPage={() =>
                     handleStartSurvey({ formik, entry: currentSurvey.entry })
                  }
               />
               {...(formik.values.pages ?? [])
                  .filter(filterPage)
                  .map((page, index, array) => (
                     <SurveyTakingPage
                        key={page.id}
                        page={page}
                        index={index}
                        currentSurvey={currentSurvey}
                        hasAuthentication={
                           currentSurvey?.is_authenticable ?? false
                        }
                        isLastPage={array?.length === index + 1}
                        onPageChange={(isNextPage) =>
                           handleChangePage({
                              currentSurvey,
                              isNextPage,
                              formik,
                           })
                        }
                        {...formik}
                        errorMap={errors}
                        isSubmitting={submitAnswersMutation.isPending}
                     />
                  ))}
               <SurveyTakingEndPage
                  currentSurvey={currentSurvey}
                  valid={!isSurveyLoading}
                  onRetry={() => {
                     submitAnswersMutation.mutate(
                        {
                           surveyId: surveyId ?? "",
                           entryId: entryIdParam ?? undefined,
                           values: formik.values,
                        },
                        {
                           onSuccess: () => {
                              addSuccessToast(
                                 t("survey.submitted.success", {
                                    ns: "notifications",
                                 }),
                              );
                              queryClient.invalidateQueries({
                                 queryKey: ["surveys-infinite"],
                              });
                              queryClient.invalidateQueries({
                                 queryKey: ["surveyResponses"],
                              });
                           },
                        },
                     );
                  }}
                  entryId={entryId}
                  isPreview={false}
                  entryIdParam={entryIdParam ?? undefined}
               />
            </SurveyTakingLayout>
         )}
      </Formik>
   ) : isSurveyLoading ? (
      <SurveyTakingSkeleton />
   ) : (
      <SurveyTakingError />
   );
};
